import { findEligibleGrants, meetsDesignationRequirement, parseAmount, parseCompanyLocation, matchesGrantRegions, parseGrantRegions } from './grantEligibility.js';
import {
  getGrantCategoryLabel,
  getGrantCategorySortRank,
  grantMatchesCategory,
  isHiringOrRdGrant,
  isMitacsGrant,
  isUnitedStatesOnlyGrant,
} from './grantCategories.js';
import { evaluateMitacsEligibility } from './mitacsEligibility.js';

/** Airtable attachment field for candidate resume on opportunity records. */
export const CANDIDATE_RESUME_FIELD = 'CandidateResume';

const pickFieldValue = (value) => {
  if (value == null) return '';
  if (typeof value === 'object' && value.value != null) return String(value.value);
  if (typeof value === 'object') return '';
  return String(value);
};

/**
 * Build company profile object for findEligibleGrants from an Airtable company record.
 */
export function companyProfileFromRecord(company) {
  const fields = company?.fields || {};
  return {
    country: fields.Country || '',
    province: fields.ProvinceOrState || '',
    locality: fields.Address || '',
    address: fields.Address || '',
    founded: fields.Founded || '',
    industry: fields.Industry || '',
    employees: fields.Employees || '',
    profitable: fields['Profitable?'] || fields.Profitable || '',
    companyType: fields.CompanyType || '',
    indigenousTeamMember: fields.IndigenousTeamMember === true,
    womenLed: fields.WomenLed === true,
  };
}

/**
 * Detailed company vs grant eligibility — returns failed check labels.
 */
export function getCompanyEligibilityDetails(company, grant, referenceData) {
  if (!company?.fields) {
    return { eligible: false, reason: 'No company selected', failedChecks: ['Company'] };
  }

  const profile = companyProfileFromRecord(company);
  const matches = findEligibleGrants(profile, [grant], referenceData);

  if (matches.length > 0) {
    return { eligible: true, reason: 'Company meets grant criteria', failedChecks: [] };
  }

  const f = grant?.fields || {};
  const { industries = {} } = referenceData;
  const failedChecks = [];

  // Re-run individual checks (mirrors grantEligibility.js)
  const companyIndustry = (profile.industry || '')
    .trim()
    .toLowerCase()
    .split(',')
    .map((el) => el.trim())
    .filter(Boolean);

  const grantIndustry = (f.Eligible_Industry || '')
    .toLowerCase()
    .split(',')
    .map((el) => el.trim())
    .filter(Boolean);

  let industryCheck = true;
  if (companyIndustry.length && companyIndustry[0]) {
    if (grantIndustry[0] !== companyIndustry[0] && grantIndustry[0] !== 'all') {
      const industryKeys = Object.keys(industries);
      industryCheck = industryKeys.some((key) => {
        const subs = Array.isArray(industries[key]) ? industries[key] : [];
        return (
          subs.some((item) => grantIndustry.includes(item)) &&
          subs.some((item) => companyIndustry.includes(item))
        );
      });
    }
  }
  if (!industryCheck) failedChecks.push('Industry');

  const locationProfile = parseCompanyLocation(profile, referenceData);
  const grantRegions = parseGrantRegions(grant);
  const locationCheck = matchesGrantRegions(grantRegions, locationProfile, referenceData);
  if (!locationCheck) failedChecks.push('Region');

  const companyEmployees = parseFloat(profile.employees);
  const employeesCheck =
    isNaN(companyEmployees) || !f.Eligible_Employees
      ? true
      : parseFloat(f.Eligible_Employees) <= companyEmployees;
  if (!employeesCheck) failedChecks.push('Employees');

  const revenueCheck =
    isNaN(companyEmployees) || !f.Eligible_Revenue
      ? true
      : parseFloat(f.Eligible_Revenue) <= companyEmployees * 100000;
  if (!revenueCheck) failedChecks.push('Revenue');

  if (!meetsDesignationRequirement(profile, f)) failedChecks.push('Designation');

  if (f.Deadline === 'Closed') failedChecks.push('Closed');

  const timeEst = f.TimeEstimateToComplete;
  const timeCheck = timeEst == null || timeEst === '' || parseFloat(timeEst) <= 90;
  if (!timeCheck) failedChecks.push('Time estimate');

  const reason =
    failedChecks.length > 0
      ? `Company fails: ${failedChecks.join(', ')}`
      : 'Company does not meet grant eligibility';

  return { eligible: false, reason, failedChecks };
}

/** Extract display metadata from a grant record. */
export function extractGrantDisplayMeta(grant) {
  const f = grant?.fields || {};
  const amount = parseAmount(f.AnnualMargin || f.Amount);
  const pSuccess = parseFloat(f.pSuccess ?? f.psuccess) || 0.65;
  const expectedValue = parseAmount(f.ExpectedValue) || amount * pSuccess;

  const excitingBenefit = pickFieldValue(
    f.ExcitingBenefit ??
      f.ExcitingBenefitAlt ??
      f.ExcitingBenefitContent ??
      f.Description_2
  );

  return {
    amount,
    pSuccess,
    successRatePct: Math.round(pSuccess * 100),
    expectedValue,
    excitingBenefit: excitingBenefit.trim(),
    deadline: f.Deadline || '',
    region: f.Eligible_Region || '',
    industry: f.Eligible_Industry || '',
    minEmployees: f.Eligible_Employees || '',
    minFounded: f.Eligible_Founded || '',
    categoryRaw: f.Category || '',
    grantUrl: f.GrantUrl || '',
  };
}

/** Summarize which grants are included in the qualification pool. */
export function summarizeGrantPool(grants = []) {
  const hiringRd = grants.filter(isHiringOrRdGrant);
  const hiring = hiringRd.filter((g) => grantMatchesCategory(g, 'hiring')).length;
  const rd = hiringRd.filter((g) => grantMatchesCategory(g, 'r&d')).length;
  const mitacs = hiringRd.filter((g) => isMitacsGrant(g)).length;
  const excludedUs = hiringRd.filter(isUnitedStatesOnlyGrant).length;
  const pool = hiringRd.filter((g) => !isUnitedStatesOnlyGrant(g)).length;

  return { hiring, rd, mitacs, excludedUs, pool, total: grants.length };
}

/** Sort: eligible → hiring/Mitacs → R&D → amount (desc) → expected value → name. */
export function compareQualificationResults(a, b) {
  if (a.overallEligible !== b.overallEligible) {
    return a.overallEligible ? -1 : 1;
  }

  const categoryA = getGrantCategorySortRank(a.grant);
  const categoryB = getGrantCategorySortRank(b.grant);
  if (categoryA !== categoryB) return categoryA - categoryB;

  const amountDiff = (b.amount || 0) - (a.amount || 0);
  if (amountDiff !== 0) return amountDiff;

  const evDiff = (b.meta?.expectedValue || 0) - (a.meta?.expectedValue || 0);
  if (evDiff !== 0) return evDiff;

  return a.grantName.localeCompare(b.grantName);
}

/**
 * Build qualification results for all hiring/R&D/Mitacs grants.
 * Returns { mitacsEval, poolSummary, results }.
 */
export function qualifyGrantsForCandidate({
  candidate,
  grants = [],
  mitacsInstitutions = [],
  company = null,
  referenceData = {},
}) {
  const mitacsEval = evaluateMitacsEligibility(candidate, mitacsInstitutions);
  const poolSummary = summarizeGrantPool(grants);
  const relevantGrants = grants.filter(
    (grant) => isHiringOrRdGrant(grant) && !isUnitedStatesOnlyGrant(grant)
  );
  const seenIds = new Set();

  const results = relevantGrants
    .filter((grant) => {
      if (seenIds.has(grant.id)) return false;
      seenIds.add(grant.id);
      return true;
    })
    .map((grant) => {
      const mitacs = isMitacsGrant(grant);
      const companyCheck = getCompanyEligibilityDetails(company, grant, referenceData);
      const meta = extractGrantDisplayMeta(grant);

      let candidateEligible = true;
      let candidateReason = 'Hiring/R&D grant — no resume-specific restrictions';

      if (mitacs) {
        candidateEligible = mitacsEval.eligible;
        candidateReason = mitacsEval.reason;
      }

      const overallEligible = candidateEligible && companyCheck.eligible;

      const reasons = [];
      if (!candidateEligible) reasons.push(candidateReason);
      if (!companyCheck.eligible) reasons.push(companyCheck.reason);

      return {
        grant,
        grantId: grant.id,
        grantName: grant.fields?.Name || 'Unknown',
        amount: meta.amount,
        category: getGrantCategoryLabel(grant),
        categoryTags: [
          ...(grantMatchesCategory(grant, 'hiring') ? ['Hiring'] : []),
          ...(grantMatchesCategory(grant, 'r&d') ? ['R&D'] : []),
          ...(mitacs ? ['Mitacs'] : []),
        ],
        isMitacs: mitacs,
        candidateEligible,
        companyEligible: companyCheck.eligible,
        overallEligible,
        reason: reasons.join(' · ') || 'Eligible',
        candidateReason,
        companyReason: companyCheck.reason,
        failedCompanyChecks: companyCheck.failedChecks || [],
        matchedInstitution: mitacs ? mitacsEval.matchedInstitution : null,
        meta,
        categorySortRank: getGrantCategorySortRank(grant),
      };
    })
    .sort(compareQualificationResults);

  return { mitacsEval, poolSummary, results };
}

/**
 * Build notes text summarizing candidate qualification context.
 */
export function buildCandidateQualificationNotes(candidate, resumeFileName) {
  const lines = ['--- Resume Qualification ---'];
  if (candidate?.candidateName) lines.push(`Candidate: ${candidate.candidateName}`);
  if (candidate?.university) lines.push(`University: ${candidate.university}`);
  if (candidate?.graduationDate) lines.push(`Graduation: ${candidate.graduationDate}`);
  if (candidate?.isCurrentlyEnrolled != null) {
    lines.push(
      `Currently enrolled: ${candidate.isCurrentlyEnrolled ? 'Yes' : 'No'}`
    );
  }
  if (resumeFileName) lines.push(`Resume file: ${resumeFileName}`);
  return lines.join('\n');
}
